import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown, Loader2, Users } from "lucide-react";

import { LinkedInMark } from "@/components/marks";

import { RoomFit } from "@/components/feed/RoomFit";
import { RoomThumb } from "@/components/feed/RoomThumb";
import { Button } from "@/components/ui/button";
import { useResearchRoomPerson, useRoomBrief, useRoomRead } from "@/lib/queries";
import { dayOf, monthOf, relativeDay, timeOf } from "@/lib/when";

/**
 * The room brief — who is in the room, before she walks into it.
 *
 * **What this screen can and cannot know, because the whole design turns on
 * it.** A full guest list is not reachable: it needs her own browser session,
 * and this runs on a server. What a logged-out fetch of an event page does
 * carry is the hosts and the guests the platform features — thirteen people
 * out of a hundred and fifty-four, in the room that prompted this feature. So
 * the card prints both numbers, always, in one line she cannot miss. Implying
 * the list is everyone would be the fintech-podcast failure with faces on it.
 *
 * **The people are behind a toggle, and that is a cost decision as much as a
 * layout one.** Opening a room spends one model call to read its bios against
 * her goal; five rooms rendered open would spend five before she had decided
 * to care about any. Collapsed by default means the scan — what is on, and how
 * well it fits — is free, and the reading is bought one room at a time.
 *
 * Two kinds of knowing, kept apart on purpose. The **room read** is the
 * model's judgement of a person's own one-line bio: instant, cheap, shallow,
 * and honest about being so. **Research** buys web search for one person on
 * her tap. Both print where they came from, because "their bio says they
 * build legal AI" and "the web says they raised last month" are different
 * claims and she is deciding whether to cross a room on them.
 */
export const Route = createFileRoute("/brief")({ component: Brief });

function Brief() {
  const brief = useRoomBrief();

  return (
    <div className="space-y-6 pb-16">
      <header className="space-y-2">
        <p className="text-2xs tracking-[0.14em] text-muted-foreground/70 uppercase">Rooms</p>
        <h1 className="font-display text-4xl leading-[1.05] tracking-tight">Room brief</h1>
        <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
          The rooms on your calendar, and who the organiser named publicly.
        </p>
      </header>

      {brief.isPending && <p className="hint">Reading your calendar…</p>}

      {brief.isError && (
        <p className="hint">Could not read your calendar just now. Try again in a moment.</p>
      )}

      {brief.data && brief.data.rooms.length === 0 && <EmptyBrief rows={brief.data.calendarRows} />}

      {brief.data && brief.data.rooms.length > 0 && (
        <ul className="space-y-4">
          {brief.data.rooms.map((room) => (
            <li key={room.id}>
              <RoomCard room={room} goal={brief.data.goal} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Three different nothings, and they need three different sentences.
 *
 * "No calendar connected" is fixable in one tap and points at Settings. "No
 * rooms this month" is just a quiet month. "Rooms, but none on a platform we
 * can read" is the honest ceiling and must not read as a bug. Collapsing these
 * into one "nothing here" is how a working feature gets reported as broken.
 */
function EmptyBrief({ rows }: { rows: number }) {
  if (rows === 0) {
    return (
      <div className="homeempty">
        <p>
          No calendar connected yet — so there are no rooms to read. Connect Google or Microsoft
          and the rooms you register for will show up here.
        </p>
        <Link to="/settings/connections" className="homeemptycta">
          Connect a calendar
        </Link>
      </div>
    );
  }

  return (
    <div className="homeempty">
      <p>
        {rows} {rows === 1 ? "event" : "events"} on your calendar this month, none of them on a
        platform with a public page we can read. Luma rooms show a brief; other hosts do not
        publish one.
      </p>
      <Link to="/feed" className="homeemptycta">
        Find rooms
      </Link>
    </div>
  );
}

type BriefRoom = NonNullable<ReturnType<typeof useRoomBrief>["data"]>["rooms"][number];
type BriefGoal = NonNullable<ReturnType<typeof useRoomBrief>["data"]>["goal"];
type PagePerson = NonNullable<BriefRoom["page"]>["people"][number];
type PersonRead = NonNullable<ReturnType<typeof useRoomRead>["data"]>["people"][number];

function RoomCard({ room, goal }: { room: BriefRoom; goal: BriefGoal }) {
  const [open, setOpen] = useState(false);
  const day = room.startsAt.slice(0, 10);
  const people = room.page?.people ?? [];

  // Only asked for once she opens the room. See the note at the top.
  const read = useRoomRead(room.url, open && people.length > 0);
  const reads = new Map((read.data?.people ?? []).map((p) => [p.name, p]));

  // The fresh read wins over the one the list was built with, so expanding a
  // room upgrades its score in place rather than waiting for a refetch.
  const verdict = read.data?.room ?? room.verdict;

  return (
    <article className="mediacard">
      <header className="flex items-start gap-3">
        <RoomThumb seed={room.id} name={room.title} />
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl leading-tight">{room.title}</h2>
          <p className="hint">
            {relativeDay(day)} · {timeOf(room.startsAt)}
            {room.location ? ` · ${room.location}` : ""}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-1">
            {/* Two claims, two chips. "You're registered" comes from a
                confirmation email; "On your calendar" is all a calendar row
                can prove. Merging them would upgrade a guess into a fact. */}
            <span className="statechip">
              {room.registered ? "You're registered" : "On your calendar"}
            </span>
            <span className="statechip unverified">{room.platform}</span>
          </p>
        </div>
        <span className="upcodate" aria-hidden>
          <b>{monthOf(day)}</b>
          <span>{dayOf(day)}</span>
        </span>
      </header>

      {/* Once she has opened a room, the model's read of it leads. Until
          then the keyword score does, labelled as a word match — the two are
          never merged into one number, because they are different claims. */}
      {verdict ? (
        <div className="mt-3">
          <p className="fitlead">
            {typeof verdict.score === "number" && (
              <b className="fitscore">{verdict.score}</b>
            )}
            <span className="fitmeta">
              <span className="fitfor">
                {verdict.kind}
                <span className="fitbasis"> · read for your goal</span>
              </span>
            </span>
          </p>
          {verdict.why && <p className="hint mt-1">{verdict.why}</p>}
          {verdict.approach.length > 0 && (
            <div className="roomhook">
              <span className="hint">Talk to · </span>
              <ul className="mt-1 space-y-0.5">
                {verdict.approach.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <RoomFit fit={room.fit} goal={goal} fitSource="keyword" className="mt-3" />
      )}

      {room.pageState === "pending" && (
        <p className="hint mt-3">Reading the event page — check back shortly.</p>
      )}

      {room.pageState === "none" && (
        <p className="hint mt-3">
          This event page isn&apos;t publicly readable, so there is nobody to name.{" "}
          <a href={room.url} target="_blank" rel="noreferrer" className="underline">
            Open it on {room.platform}
          </a>
        </p>
      )}

      {room.page && people.length === 0 && (
        <p className="hint mt-3">
          {room.page.attendeeCount
            ? `${room.page.attendeeCount} going. The organiser named nobody publicly, so the room itself is the brief.`
            : "The organiser named nobody publicly, so the room itself is the brief."}
        </p>
      )}

      {room.page && people.length > 0 && (
        <section className="mt-3">
          {/* The honesty line, printed whether or not the list is open: the
              gap between named and going is the first thing she should know
              about this card, not something revealed by expanding it. */}
          <p className="hint flex items-center gap-1.5">
            <Users size={13} aria-hidden />
            <span>
              <b>{room.page.named}</b> named publicly
              {room.page.attendeeCount ? ` of ${room.page.attendeeCount} going` : ""} — hosts and
              the guests the organiser features. Full guest lists aren&apos;t public.
            </span>
          </p>

          <button
            type="button"
            className="roomtoggle mt-2"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronDown size={15} aria-hidden />
            {open ? "Hide the room" : `See who's going (${people.length})`}
          </button>

          {open && (
            <div className="mt-3 space-y-2">
              {read.isPending && (
                <p className="hint flex items-center gap-1.5">
                  <Loader2 className="animate-spin" size={13} aria-hidden />
                  Reading their bios against your goal…
                </p>
              )}
              {read.data?.mode === "no_model" && (
                <p className="hint">
                  No model available, so nobody is scored — the page&apos;s own words are below.
                </p>
              )}
              <ul className="space-y-2">
                {people.map((person, i) => (
                  <li key={`${person.name}-${i}`}>
                    <PersonRow
                      person={person}
                      read={reads.get(person.name)}
                      roomUrl={room.url}
                      roomTitle={room.title}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </article>
  );
}

function PersonRow({
  person,
  read,
  roomUrl,
  roomTitle,
}: {
  person: PagePerson;
  read: PersonRead | undefined;
  roomUrl: string;
  roomTitle: string;
}) {
  const research = useResearchRoomPerson();
  const found = research.data?.enrichment;
  const deep = found?.goalFit;

  // Proxied, never the CDN address: a browser fetching Luma's avatars directly
  // would tell Luma which people she is sizing up before a room. Same decision
  // as podcast artwork.
  const avatar = person.avatar
    ? `/api/room-brief/avatar?url=${encodeURIComponent(roomUrl)}&name=${encodeURIComponent(person.name)}`
    : null;

  // Their role as the model read it off their own bio, falling back to nothing
  // — never to a guess from the name.
  const role = read?.role ?? null;
  const company = read?.company ?? null;

  return (
    <div className="roomperson">
      <div className="flex items-start gap-3">
        <RoomThumb
          seed={person.name}
          name={person.name}
          imageUrl={avatar}
          className="size-10 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <b className="text-sm">{person.name}</b>
            {/* Where on the page they appeared. "Host" and "also going" are
                different claims — the same reason a network is never printed
                as a show's host. */}
            <span className="statechip">{person.role === "host" ? "Host" : "Also going"}</span>
            {/* Only ever printed when their own bio says they invest. There is
                no "not an investor" chip: a blank bio and a non-investor are
                the same fact here, and she may pitch a round on this one. */}
            {read?.investor && <span className="statechip unverified">Investor</span>}
          </p>
          {(role || company) && (
            <p className="text-sm text-muted-foreground">
              {[role, company].filter(Boolean).join(" · ")}
            </p>
          )}
          {/* Their own words, verbatim. An absent bio stays absent. */}
          {person.bio && <p className="hint mt-0.5">{person.bio}</p>}
        </div>

        {/* The number the room read produced, if it produced one. No score is
            a real state and reads as absence rather than as a zero. */}
        {typeof read?.score === "number" && (
          <span className="shrink-0 text-right">
            <b className="fitscore">{read.score}</b>
          </span>
        )}
      </div>

      {read?.why && <p className="hint mt-2">{read.why}</p>}

      {read?.hook && (
        <p className="roomhook">
          <span className="hint">Opener · </span>
          {read.hook}
        </p>
      )}

      {deep && (
        <div className="mt-2">
          <p className="fitlead">
            <b className="fitscore">{deep.score}</b>
            <span className="fitmeta">
              <span className="fitfor">match · researched on the web</span>
            </span>
          </p>
          <ul className="mt-1 space-y-0.5">
            {deep.reasons.map((reason, i) => (
              <li key={i} className="hint">
                · {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {research.data && !deep && (
        <p className="hint mt-2">
          {research.data.mode === "empty"
            ? "Nothing public we could tie to them with confidence."
            : "Found them, but nothing that scores against your goal."}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {person.linkedin && (
          <Button variant="outline" size="sm" asChild>
            <a
              href={`https://www.linkedin.com${person.linkedin}`}
              target="_blank"
              rel="noreferrer"
            >
              <LinkedInMark size={13} /> LinkedIn
            </a>
          </Button>
        )}
        {!research.data && (
          <Button
            variant="outline"
            size="sm"
            disabled={research.isPending}
            onClick={() =>
              research.mutate({
                name: person.name,
                ...(company ? { org: company } : {}),
                ...(role ? { role } : {}),
              })
            }
          >
            {research.isPending ? (
              <>
                <Loader2 className="animate-spin" size={13} aria-hidden /> Researching…
              </>
            ) : (
              "Research"
            )}
          </Button>
        )}
        {person.website && (
          <a href={person.website} target="_blank" rel="noreferrer" className="hint underline">
            Site
          </a>
        )}
        {person.username && (
          <a
            href={`https://lu.ma/user/${person.username}`}
            target="_blank"
            rel="noreferrer"
            className="hint underline"
          >
            Profile
          </a>
        )}
      </div>

      {research.isError && <p className="hint mt-2">Research is unavailable right now.</p>}

      <span className="sr-only">{roomTitle}</span>
    </div>
  );
}
